import { useContext, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"

import { makeStyles } from "@mui/styles"
import Paper from "@mui/material/Paper"

import MainContainer from "../../components/MainContainer"
import MainHeader from "../../components/MainHeader"
import Title from "../../components/Title"

import ReportIcon from "@mui/icons-material/Report"

import {
    Box,
    Button,
    Grid2,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import api from "../../services/api"
import { has, get, isNull } from "lodash"
import CardCounter from "../../components/Dashboard/CardCounter"
import GroupIcon from "@mui/icons-material/Group"
import ScheduleIcon from "@mui/icons-material/Schedule"
import EventAvailableIcon from "@mui/icons-material/EventAvailable"
import DoneIcon from "@mui/icons-material/Done"
import DoneAllIcon from "@mui/icons-material/DoneAll"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import ListAltIcon from "@mui/icons-material/ListAlt"
import { useDate } from "../../hooks/useDate"

import { SocketContext } from "../../context/Socket/SocketContext"
import { HourglassTop } from "@mui/icons-material"
import TableRowSkeleton from "../../components/TableRowSkeleton"
import { i18n } from "../../translate/i18n"
import { format } from "date-fns"

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    textRight: {
        textAlign: "right",
    },
    tabPanelsContainer: {
        padding: theme.spacing(2),
    },
}))

const CampaignReport = () => {
    const classes = useStyles()

    const { campaignId } = useParams()

    const [campaign, setCampaign] = useState({})
    const [contactCount, setContactCount] = useState(0)
    const [validContacts, setValidContacts] = useState(0)
    const [delivered, setDelivered] = useState(0)
    const [confirmationRequested, setConfirmationRequested] = useState(0)
    const [confirmed, setConfirmed] = useState(0)
    const [percent, setPercent] = useState(0)
    const [loading, setLoading] = useState(false)

    const [shipping, setShipping] = useState([])
    const [errorCount, setErrorCount] = useState(0)

    const [pageNumber, setPageNumber] = useState(1)
    const [hasMore, setHasMore] = useState(false)

    const { socket } = useContext(SocketContext)

    const { datetimeToClient } = useDate()

    useEffect(() => {
        setLoading(true)
        findCampaign()
    }, [pageNumber])

    useEffect(() => {
        setPercent(Math.ceil((delivered / validContacts) * 100))
    }, [delivered, validContacts])

    useEffect(() => {
        const companyId = localStorage.getItem("companyId")
        socket.on(`company-${companyId}-${campaignId}-shipping`, (data) => {
            if (data.action === "create") {
                const arr = [...shipping]
                arr.unshift(data.record)
                setShipping(arr)
            }

            if (data.action === "update") {
                if (data.record.deliveredAt) {
                    setDelivered((prevState) => prevState + 1)
                }
                if (data.record.hasError) {
                    setErrorCount((prevState) => prevState + 1)
                }
                setShipping((prev) =>
                    prev.map((item) =>
                        item.id === data.record.id ? data.record : item,
                    ),
                )
            }
        })

        socket.on(
            `company-${companyId}-${campaignId}-shipping-count`,
            (data) => {
                if (data.action === "delivered") {
                    setDelivered(data.record)
                }
                if (data.action === "error") {
                    setErrorCount(data.record)
                }
            },
        )
    }, [socket])

    useEffect(() => {
        const companyId = localStorage.getItem("companyId")

        socket.on(`company-${companyId}-campaign`, (data) => {
            if (data.record?.id === +campaignId) {
                setCampaign(data.record)

                if (data.record.status === "FINALIZADA") {
                    setTimeout(() => {
                        findCampaign()
                    }, 5000)
                }
            }
        })
    }, [campaignId, socket])

    function setMoreShippings(shippings = []) {
        const ships = shippings
        const newData = []

        ships.forEach((contact) => {
            const contactIndex = shipping.findIndex((c) => c.id === contact.id)
            if (contactIndex !== -1) {
                shipping[contactIndex] = contact
            } else {
                newData.unshift(contact)
            }
        })

        setShipping([...shipping, ...newData])
    }

    const findCampaign = async () => {
        setLoading(true)
        const { data } = await api.get(`/campaigns/${campaignId}`)
        const { data: shippingData } = await api.get(
            `/campaignShipping/list/?campaignId=${campaignId}&pageNumber=${pageNumber}`,
        )

        setMoreShippings(shippingData.records)
        setHasMore(shippingData.hasMore)

        setValidContacts(data.validContactsCount)
        setContactCount(data.totalContactsCount)
        setDelivered(data.shippingCount)
        setErrorCount(data.errorCount)

        setCampaign(data.campaign)
        setLoading(false)
    }

    const formatStatus = (val) => {
        switch (val) {
            case "INATIVA":
                return "Inativa"
            case "PROGRAMADA":
                return "Programada"
            case "EM_ANDAMENTO":
                return "Em Andamento"
            case "CANCELADA":
                return "Cancelada"
            case "FINALIZADA":
                return "Finalizada"
            default:
                return val
        }
    }

    const shippingStatus = ({ deliveredAt, hasError }) => {
        if (hasError) {
            return "error"
        }
        if (deliveredAt) {
            return "delivered"
        }
        if (!hasError && !deliveredAt) {
            return "waiting"
        }
    }

    const loadMore = () => {
        if (!loading && hasMore) {
            setPageNumber((prev) => prev + 1)
        }
    }

    return (
        <MainContainer>
            <MainHeader>
                <Grid2 style={{ width: "99.6%" }} container>
                    <Grid2 size={{ xs: 12 }}>
                        <Title>
                            Relatório da {campaign.name || "Campanha"}
                        </Title>
                    </Grid2>
                </Grid2>
            </MainHeader>
            <Paper
                className={classes.mainPaper}
                variant="outlined"
                // onScroll={handleScroll}
            >
                <Typography variant="h6" component="h2">
                    Status: {formatStatus(campaign.status)} {delivered} de{" "}
                    {validContacts}
                </Typography>
                <Grid2 spacing={2} container>
                    <Grid2 size={{ xs: 12, md: 11 }}>
                        <LinearProgress
                            variant="determinate"
                            style={{
                                height: 15,
                                borderRadius: 8,
                                margin: "20px 0",
                            }}
                            value={percent}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 1 }}>
                        <Typography my="16px">{percent || 0}%</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <CardCounter
                            icon={<GroupIcon fontSize="inherit" />}
                            title="Contatos Válidos"
                            value={`${validContacts} de ${contactCount}`}
                            loading={loading}
                        />
                    </Grid2>
                    {campaign.confirmation && (
                        <>
                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <CardCounter
                                    icon={<DoneIcon fontSize="inherit" />}
                                    title="Confirmações Solicitadas"
                                    value={confirmationRequested}
                                    loading={loading}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <CardCounter
                                    icon={<DoneAllIcon fontSize="inherit" />}
                                    title="Confirmações"
                                    value={confirmed}
                                    loading={loading}
                                />
                            </Grid2>
                        </>
                    )}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <CardCounter
                            icon={<CheckCircleIcon fontSize="inherit" />}
                            title="Entregues"
                            value={delivered}
                            loading={loading}
                        />
                    </Grid2>
                    {campaign.whatsappId && (
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <CardCounter
                                icon={<WhatsAppIcon fontSize="inherit" />}
                                title="Conexão"
                                value={campaign.whatsapp.name}
                                loading={loading}
                            />
                        </Grid2>
                    )}

                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <CardCounter
                            icon={<ListAltIcon fontSize="inherit" />}
                            title="Lista de Contatos"
                            value={
                                campaign.contactList?.name ||
                                "Nenhuma lista selecionada"
                            }
                            loading={loading}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <CardCounter
                            icon={<ScheduleIcon fontSize="inherit" />}
                            title="Agendamento"
                            value={datetimeToClient(campaign.scheduledAt)}
                            loading={loading}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <CardCounter
                            icon={<EventAvailableIcon fontSize="inherit" />}
                            title="Conclusão"
                            value={
                                campaign.completedAt
                                    ? datetimeToClient(campaign.completedAt)
                                    : "-"
                            }
                            loading={loading}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <CardCounter
                            icon={<ReportIcon fontSize="inherit" />}
                            title="Erros"
                            value={errorCount}
                            loading={loading}
                        />
                    </Grid2>
                </Grid2>

                <Grid2 spacing={2} container>
                    <Typography variant="h5" component="h5" sx={{ mt: 2 }}>
                        Envios
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    style={{ width: "0%" }}
                                >
                                    #
                                </TableCell>
                                <TableCell>
                                    {i18n.t("contactListItems.table.name")}
                                </TableCell>
                                <TableCell align="center">
                                    {i18n.t("contactListItems.table.number")}
                                </TableCell>
                                <TableCell align="center">Mensagem</TableCell>
                                <TableCell align="center">Enviada em</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <>
                                {shipping?.map((ship) => {
                                    const status = shippingStatus({
                                        deliveredAt: ship.deliveredAt,
                                        hasError: ship.hasError,
                                    })
                                    return (
                                        <TableRow key={ship.id}>
                                            <TableCell
                                                align="center"
                                                style={{ width: "0%" }}
                                            >
                                                <IconButton>
                                                    {status === "error" && (
                                                        <Tooltip
                                                            title={
                                                                ship.errorLog
                                                            }
                                                            arrow
                                                            placement="bottom"
                                                        >
                                                            <ReportIcon color="error" />
                                                        </Tooltip>
                                                    )}
                                                    {status === "delivered" && (
                                                        <CheckCircleIcon
                                                            titleAccess="Entregue"
                                                            color="success"
                                                        />
                                                    )}
                                                    {status === "waiting" && (
                                                        <HourglassTop
                                                            titleAccess="Processando"
                                                            color="primary"
                                                        />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                {ship.contact?.name}
                                            </TableCell>
                                            <TableCell align="center">
                                                {ship.contact?.number}
                                            </TableCell>
                                            <TableCell align="center">
                                                {ship.message}
                                            </TableCell>
                                            <TableCell align="center">
                                                {ship.deliveredAt
                                                    ? format(
                                                          ship?.deliveredAt,
                                                          "dd/MM/yyyy HH:mm",
                                                      )
                                                    : ""}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                {loading && <TableRowSkeleton columns={4} />}
                            </>
                        </TableBody>
                    </Table>

                    {hasMore && (
                        <Box
                            width="100%"
                            display="flex"
                            justifyContent="center"
                        >
                            <Button onClick={loadMore}>
                                Carregar mais itens
                            </Button>
                        </Box>
                    )}
                </Grid2>
            </Paper>
        </MainContainer>
    )
}

export default CampaignReport
